import React, { useState, useEffect, useRef, useMemo } from 'react';
import { apiGet, apiPost, apiPut } from './apiUtils';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table';
import { Search, Calendar, ArrowUp, Edit, Save, X, Trash2, PackageSearch, GripVertical } from 'lucide-react';
import useBusinessDate from './hooks/useBusinessDate';
import { formatDateDash } from './utils/formatters';
import { toast } from 'sonner';

function StockOnboarding({ isAuthenticated }) {
  const businessDateISO = useBusinessDate(); // Returns YYYY-MM-DD format
  const businessDate = formatDateDash(businessDateISO); // Convert to DD-MM-YYYY for display
  
  const [masterBrands, setMasterBrands] = useState([]);
  const [shopInventory, setShopInventory] = useState([]);
  const [filteredBrands, setFilteredBrands] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedItems, setEditedItems] = useState({});
  const searchContainerRef = useRef(null);
  
  // Drag and drop state
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const inventoryTableRef = useRef(null);
  
  // Filter out products with zero stock activity (O.S, Received, and Total all zero)
  const activeShopInventory = useMemo(() => {
    return (shopInventory || []).filter(item => {
      const openingStock = item.openingStock || 0;
      const receivedStock = item.receivedStock || 0;
      const totalStock = item.totalStock || 0;
      // Include item if any of these values is non-zero
      return openingStock !== 0 || receivedStock !== 0 || totalStock !== 0;
    });
  }, [shopInventory]);

  // Bottle counts grouped by size bucket
  // (QQ / 700ml / PP / 275ml / 250ml / 200ml / NN / DD / 2L / 1L / OO / 50ml / Beers / Cans / Mini)
  const sizeBreakdown = useMemo(() => {
    const buckets = [
      { key: 'QQ',    label: 'QQ',    sub: '750ml', match: (it) => parseInt(it.size, 10) === 750 },
      { key: '700ML', label: '700ml', sub: '700ml', match: (it) => parseInt(it.size, 10) === 700 },
      { key: 'PP',    label: 'PP',    sub: '375ml', match: (it) => parseInt(it.size, 10) === 375 },
      { key: '275ML', label: '275ml', sub: '275ml', match: (it) => parseInt(it.size, 10) === 275 },
      { key: '250ML', label: '250ml', sub: '250ml', match: (it) => parseInt(it.size, 10) === 250 },
      { key: '200ML', label: '200ml', sub: '200ml', match: (it) => parseInt(it.size, 10) === 200 },
      { key: 'NN',    label: 'NN',    sub: '180ml', match: (it) => parseInt(it.size, 10) === 180 },
      { key: 'DD',    label: 'DD',    sub: '90ml',  match: (it) => parseInt(it.size, 10) === 90 },
      { key: '2L',    label: '2L',    sub: '2000ml', match: (it) => parseInt(it.size, 10) === 2000 },
      { key: '1L',    label: '1L',    sub: '1000ml', match: (it) => parseInt(it.size, 10) === 1000 },
      { key: 'OO',    label: 'OO',    sub: '60ml',  match: (it) => parseInt(it.size, 10) === 60 },
      { key: '50ML',  label: '50ml',  sub: '50ml',  match: (it) => parseInt(it.size, 10) === 50 },
      { key: 'BEERS', label: 'Beers', sub: '650ml', match: (it) => parseInt(it.size, 10) === 650 },
      { key: 'CANS',  label: 'Cans',  sub: '500ml', match: (it) => parseInt(it.size, 10) === 500 },
      { key: 'MINI',  label: 'Mini',  sub: '330ml', match: (it) => parseInt(it.size, 10) === 330 },
    ];

    const rows = buckets.map(bucket => {
      const total = (activeShopInventory || []).reduce((sum, item) => {
        if (!bucket.match(item)) return sum;
        const stock = item.closingStock ?? item.totalStock ?? 0;
        return sum + (Number(stock) || 0);
      }, 0);
      return { ...bucket, total };
    });

    const grandTotal = rows.reduce((sum, b) => sum + b.total, 0);
    return { rows, grandTotal };
  }, [activeShopInventory]);

  useEffect(() => {
    fetchMasterBrands();
    fetchShopInventory();
  }, []);

  // Smart refresh - only when user returns to the page
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && !isEditMode) {
        // User returned to the page, refresh inventory (but not during edit mode)
        fetchShopInventory();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isEditMode]);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchMasterBrands = async () => {
    try {
      // Fetch all master brands without pack type filter
      const response = await apiGet('/api/master-brands');
      const brands = await response.json();
        setMasterBrands(brands);
    } catch (error) {
      console.error('Error fetching master brands:', error);
    }
  };

  const fetchShopInventory = async () => {
    try {
      const response = await apiGet('/api/shop/products');
      const data = await response.json();
      const products = data.products || [];
      console.log('📦 Fetched shop inventory:', products.length, 'products');
      if (products.length > 0) {
        console.log('📦 Sample product:', products[0]);
      }
      setShopInventory(products);
    } catch (error) {
      console.error('Error fetching shop inventory:', error);
    }
  };




  const handleSearchChange = async (value) => {
    setSearchTerm(value);
    if (value.trim() === '') {
      setFilteredBrands([]);
      setShowSearchResults(false);
    } else {
      try {
        const response = await apiGet(`/api/search-brands?q=${encodeURIComponent(value)}`);
        const data = await response.json();
        
        const searchResults = (data.brands || []).map(brand => ({
          id: brand.id,
          brandNumber: brand.brand_number,
          name: brand.brand_name,
          size: brand.size_ml,
          sizeCode: brand.size_code,
          mrp: brand.standard_mrp,
          packQuantity: brand.pack_quantity,
          packType: brand.pack_type,
          productType: brand.product_type,
          brandKind: brand.brand_kind,
          category: brand.category,
          siblingMasterBrandId: brand.sibling_master_brand_id,
          gVariantId: brand.g_variant_id,
          pVariantId: brand.p_variant_id
        }));
        
        // Filter out products that have opening stock > 0 (already onboarded)
        // IMPORTANT: Only compare non-null values to avoid null === null matching
        const filtered = searchResults.filter(brand => {
          const inventoryItem = shopInventory.find(inv => 
            inv.master_brand_id === brand.id ||
            (brand.siblingMasterBrandId && inv.master_brand_id === brand.siblingMasterBrandId) ||
            (inv.sibling_master_brand_id && inv.sibling_master_brand_id === brand.id) ||
            (inv.sibling_master_brand_id && brand.siblingMasterBrandId && inv.sibling_master_brand_id === brand.siblingMasterBrandId)
          );
          
          const hasOpeningStock = inventoryItem?.openingStock > 0;
          return !hasOpeningStock;
        });
        
        setFilteredBrands(filtered);
        setShowSearchResults(true);
      } catch (error) {
        console.error('Error searching brands:', error);
        // Fallback to local filtering if API fails
        const searchTermLower = value.toLowerCase();
        const searchFiltered = masterBrands.filter(brand => {
          return brand.name.toLowerCase().includes(searchTermLower) ||
            brand.brandNumber.toLowerCase().includes(searchTermLower);
        });
        setFilteredBrands(searchFiltered.slice(0, 20));
        setShowSearchResults(true);
      }
    }
  };

  const handleProductSelect = (product) => {
    // Check if this exact product already exists (same master brand ID or sibling)
    const exists = selectedProducts.find(p => 
      p.id === product.id || 
      p.id === product.siblingMasterBrandId ||
      p.siblingMasterBrandId === product.id
    );
    if (!exists) {
      const newProduct = {
        ...product,
        quantity: 0,
        markup: 0,
        // Include sibling info for combined G&P products
        siblingMasterBrandId: product.siblingMasterBrandId
      };
      setSelectedProducts([...selectedProducts, newProduct]);
    }
    // Don't close search results or clear search term - keep it open for more additions
  };

  const handleQuantityChange = (productId, value) => {
    setSelectedProducts(products =>
      products.map(p =>
        p.id === productId ? { ...p, quantity: parseInt(value) || 0 } : p
      )
    );
  };

  const handleMarkupChange = (productId, value) => {
    setSelectedProducts(products =>
      products.map(p =>
        p.id === productId ? { ...p, markup: parseFloat(value) || 0 } : p
      )
    );
  };

  const removeProduct = (productId) => {
    setSelectedProducts(products => products.filter(p => p.id !== productId));
  };

  // Edit mode functions
  const toggleEditMode = () => {
    if (isEditMode) {
      // Exiting edit mode - reset edited items
      setEditedItems({});
    }
    setIsEditMode(!isEditMode);
  };

  // Removed quantity editing - only allow markup editing
  // const handleEditQuantityChange = (itemId, value) => {
  //   setEditedItems(prev => ({
  //     ...prev,
  //     [itemId]: {
  //       ...prev[itemId],
  //       quantity: parseInt(value) || 0
  //     }
  //   }));
  // };

  const handleEditMarkupChange = (itemId, value) => {
    setEditedItems(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        markup: value === '' ? '' : (parseFloat(value) || 0)
      }
    }));
  };

  const saveEditedItems = async () => {
    const itemsToUpdate = Object.keys(editedItems).filter(itemId => 
      editedItems[itemId].markup !== undefined
    );

    if (itemsToUpdate.length === 0) {
      toast.error('No price changes to save');
      return;
    }

    setSaving(true);
    try {
      // Filter out stale items and build updates
      const updates = [];
      const staleItems = [];
      
      for (const itemId of itemsToUpdate) {
        // Compare as strings since backend returns id as string
        const item = shopInventory.find(inv => String(inv.id) === String(itemId));
        
        // Skip items that are no longer in the inventory (stale edits)
        if (!item) {
          console.warn(`⚠️ Item with ID ${itemId} not found in current shop inventory (stale edit)`);
          staleItems.push(itemId);
          continue;
        }
        
        const edits = editedItems[itemId];
        const markupValue = edits.markup !== undefined ? (edits.markup === '' ? 0 : parseFloat(edits.markup)) : (parseFloat(item.markup_price) || 0);
        
        updates.push({
          shopInventoryId: parseInt(itemId), // Backend expects integer
          markup: markupValue
        });
      }
      
      // Remove stale items from editedItems
      if (staleItems.length > 0) {
        setEditedItems(prev => {
          const cleaned = { ...prev };
          staleItems.forEach(id => delete cleaned[id]);
          return cleaned;
        });
      }
      
      // Check if we have any valid updates left
      if (updates.length === 0) {
        toast.error('No valid price changes to save. Items may have been removed from inventory.');
        setSaving(false);
        return;
      }

      const response = await apiPost('/api/shop/inventory/price-update', {
        updates: updates
      });

      if (response.ok) {
        toast.success('Prices updated successfully!');
        setIsEditMode(false);
        setEditedItems({});
        // Add a small delay to ensure database transaction is committed
        await new Promise(resolve => setTimeout(resolve, 500));
        await fetchShopInventory(); // Refresh inventory
      } else {
        const error = await response.json();
        console.error('❌ Backend error:', error);
        toast.error(`Error: ${error.message || 'Failed to update prices'}`);
      }
    } catch (error) {
      console.error('Error updating prices:', error);
      toast.error(`Error: ${error.message || 'Network error during price update'}`);
    }
    setSaving(false);
  };

  const cancelEdit = () => {
    setEditedItems({});
    setIsEditMode(false);
  };

  const handleSaveStockOnboarding = async () => {
    if (selectedProducts.length === 0) {
      toast.error('Please select at least one product to onboard');
      return;
    }

    // Validate all products have quantity >= 0 (allow 0 for markup-only updates)
    const invalidProducts = selectedProducts.filter(p => p.quantity < 0 || isNaN(p.quantity));
    if (invalidProducts.length > 0) {
      toast.error('Please enter a valid quantity (0 or greater) for all products');
      return;
    }

    setSaving(true);
    try {
      // Use the businessDateISO from the hook (already in YYYY-MM-DD format)
      const response = await apiPost('/api/stock-onboarding/save', {
        products: selectedProducts,
        businessDate: businessDateISO
      });

      if (response.ok) {
        const result = await response.json();
        
        toast.success(`Stock onboarding completed!\n\n✅ Products added: ${result.results.inventoryUpdated}\n✅ Opening stock updated: ${result.results.openingStockUpdated}`, {
          duration: 5000
        });
        
        // Reset form
        setSelectedProducts([]);
        setSearchTerm('');
        setShowSearchResults(false);
        
        // Refresh shop inventory to update search results
        await fetchShopInventory();
        
        // Scroll to top of the page to show updated inventory
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const error = await response.json();
        toast.error(`Error: ${error.message || 'Server error during stock onboarding'}`);
      }
    } catch (error) {
      console.error('Error saving stock onboarding:', error);
      toast.error(`Error: ${error.message || 'Network error during stock onboarding'}`);
    }
    setSaving(false);
  };

  // Helper function to format numbers with commas (for currency)
  const formatNumber = (num) => {
    return num.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Helper function to format quantities (integers, no decimals)
  const formatQuantity = (num) => {
    return num.toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  // Helper function to get markup value safely (handles empty strings)
  const getMarkupValue = (itemId, fallbackValue = 0) => {
    const editedMarkup = editedItems[itemId]?.markup;
    if (editedMarkup !== undefined) {
      return editedMarkup === '' ? 0 : parseFloat(editedMarkup) || 0;
    }
    return parseFloat(fallbackValue) || 0;
  };

  // Helper function to group inventory items by brand name (aggregated display)
  const groupInventoryByBrand = (inventory) => {
    const grouped = {};
    let serialCounter = 1;
    
    inventory.forEach((item) => {
      const brandKey = item.name; // Group by brand name only (aggregated)
      if (!grouped[brandKey]) {
        grouped[brandKey] = {
          brandName: item.name,
          brandNumber: item.brandNumber,
          packTypes: [], // Track all pack types for this brand
          items: [],
          serialNumber: serialCounter++
        };
      }
      
      // Add pack types from the aggregated item (which contains packTypes array)
      if (item.packTypes && Array.isArray(item.packTypes)) {
        item.packTypes.forEach(packType => {
          if (!grouped[brandKey].packTypes.includes(packType)) {
            grouped[brandKey].packTypes.push(packType);
          }
        });
      }
      
      grouped[brandKey].items.push(item);
    });
    
    // Sort items within each brand group by size (ml) in descending order
    Object.values(grouped).forEach(brandGroup => {
      brandGroup.items.sort((a, b) => {
        const sizeA = parseFloat(a.size) || 0;
        const sizeB = parseFloat(b.size) || 0;
        return sizeB - sizeA; // Descending order (largest first)
      });
    });
    
    return Object.values(grouped);
  };

  // Removed drag and drop auto-scroll functions - no longer needed with up arrow buttons

  // Move item to top function
  const moveToTop = (itemId) => {
    const currentInventory = [...(shopInventory || [])];
    const itemIndex = currentInventory.findIndex(item => item.id === itemId);
    
    if (itemIndex === -1) {
      return;
    }
    
    if (itemIndex === 0) {
      return;
    }
    
    // Remove item from current position
    const [movedItem] = currentInventory.splice(itemIndex, 1);
    
    // Add item to the beginning
    currentInventory.unshift(movedItem);
    
    setShopInventory(currentInventory);
    
    // Save the new sort order to the backend
    saveSortOrder(currentInventory);
  };

  // Save sort order to backend
  const saveSortOrder = async (inventory) => {
    try {
      // Group inventory by brand and create the sorted brand groups structure
      const brandGroups = groupInventoryByBrand(inventory);
      
      const sortedBrandGroups = brandGroups.map((brandGroup, groupIndex) => ({
        brandName: brandGroup.brandName,
        productIds: brandGroup.items.map(item => item.id),
        groupOrder: groupIndex + 1
      }));

      // Use apiPut with extended timeout (handled in apiUtils)
      const response = await apiPut('/api/shop/update-sort-order', {
        sortedBrandGroups: sortedBrandGroups
      });

      if (response.ok) {
        // Refresh inventory to reflect the new sort order from database
        await fetchShopInventory();
      }
    } catch (error) {
      // Don't show alert for sort order errors to avoid disrupting UX
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, item) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget);
  };

  const handleDragOver = (e, item) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (draggedItem && draggedItem.id !== item.id) {
      setDragOverItem(item);
    }
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleDrop = (e, targetItem) => {
    e.preventDefault();
    
    if (!draggedItem || draggedItem.id === targetItem.id) {
      return;
    }

    const currentInventory = [...(shopInventory || [])];
    
    // Find the indices
    const draggedIndex = currentInventory.findIndex(item => item.id === draggedItem.id);
    const targetIndex = currentInventory.findIndex(item => item.id === targetItem.id);
    
    if (draggedIndex === -1 || targetIndex === -1) {
      return;
    }
    
    // Remove dragged item
    const [removed] = currentInventory.splice(draggedIndex, 1);
    
    // Insert at target position
    currentInventory.splice(targetIndex, 0, removed);
    
    setShopInventory(currentInventory);
    setDraggedItem(null);
    setDragOverItem(null);
    
    // Save the new order
    saveSortOrder(currentInventory);
  };

  // Add event listeners to prevent wheel and arrow key changes
  useEffect(() => {
    const inputs = document.querySelectorAll('.quantity-input, .markup-input, .edit-markup-input');
    
    const preventWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    };
    
    const preventArrowKeys = (e) => {
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    inputs.forEach(input => {
      input.addEventListener('wheel', preventWheel, { passive: false });
      input.addEventListener('keydown', preventArrowKeys, { passive: false });
    });

    return () => {
      inputs.forEach(input => {
        input.removeEventListener('wheel', preventWheel);
        input.removeEventListener('keydown', preventArrowKeys);
      });
    };
  }, [selectedProducts, shopInventory, isEditMode]);

  // Cleanup drag state when component unmounts or edit mode changes
  useEffect(() => {
    return () => {
      setDraggedItem(null);
      setDragOverItem(null);
    };
  }, []);

  // Reset drag state when entering edit mode
  useEffect(() => {
    if (isEditMode) {
      setDraggedItem(null);
      setDragOverItem(null);
    }
  }, [isEditMode]);

  return (
    <main className="w-full px-4 py-8 space-y-8 max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 w-full">
          <div className="space-y-2 text-left">
            <h1 className="text-4xl font-bold text-left">Stock Onboarding</h1>
            <p className="text-base text-muted-foreground text-left">Add new products to your inventory with initial stock quantities</p>
          </div>
          <div className="flex items-center gap-2 px-4 py-3 rounded-lg border bg-card">
            <Calendar className="h-4 w-4" />
            <div className="text-left">
              <div className="text-xs text-muted-foreground">Business Date</div>
              <div className="text-base font-semibold">{businessDate}</div>
            </div>
          </div>
        </div>
        
        {/* Search Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Search Products</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative" ref={searchContainerRef}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  className="pl-10"
                  placeholder="Search by brand name, number, or size code..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onFocus={() => {
                    if (searchTerm.trim() !== '' && filteredBrands.length > 0) {
                      setShowSearchResults(true);
                    }
                  }}
                  autoCapitalize="off"
                  autoCorrect="off"
                  autoComplete="off"
                  spellCheck="false"
                />
              </div>
              
              {showSearchResults && filteredBrands.length > 0 && (
                <Card className="absolute z-50 w-full mt-2 max-h-96 overflow-auto">
                  <CardContent className="p-2">
                    {filteredBrands.map(brand => {
                      const isAlreadyAdded = selectedProducts.some(p => p.id === brand.id);
                      return (
                        <div 
                          key={brand.id} 
                          className="flex items-center justify-between p-3 hover:bg-accent rounded-md transition-colors"
                        >
                          <span className="text-sm">
                            <span className="font-semibold">#{brand.brandNumber}</span> | {brand.name} | {brand.packType} | {brand.packQuantity} × {brand.size}ml | ₹{brand.mrp}
                          </span>
                          <Button 
                            size="sm"
                            variant={isAlreadyAdded ? "secondary" : "default"}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleProductSelect(brand);
                            }}
                            disabled={isAlreadyAdded}
                            className="ml-4"
                          >
                            {isAlreadyAdded ? 'Added' : 'Add'}
                          </Button>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Bottles by Size */}
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="space-y-1">
                <CardTitle className="text-lg">Bottles by Size</CardTitle>
                <p className="text-sm text-muted-foreground">Total bottles in current inventory, broken down by size</p>
              </div>
              <Badge variant="secondary" className="px-3 py-2">
                <span className="text-xs font-medium">Grand Total: {formatQuantity(sizeBreakdown.grandTotal)} bottles</span>
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {sizeBreakdown.rows.map(bucket => {
                const isEmpty = bucket.total === 0;
                return (
                  <Card
                    key={bucket.key}
                    className={
                      isEmpty
                        ? 'border-dashed opacity-60'
                        : 'bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950 dark:to-purple-900 border-purple-200 dark:border-purple-800'
                    }
                  >
                    <CardContent className="p-4">
                      <div className="flex flex-col items-center text-center">
                        <span className={`text-xs font-semibold uppercase tracking-wider ${isEmpty ? 'text-muted-foreground' : 'text-purple-600 dark:text-purple-300'}`}>
                          {bucket.label}
                        </span>
                        <span className="text-[10px] text-muted-foreground mt-0.5">{bucket.sub}</span>
                        <span className={`text-2xl font-bold mt-1 ${isEmpty ? 'text-muted-foreground' : 'text-purple-900 dark:text-purple-100'}`}>
                          {formatQuantity(bucket.total)}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Current Shop Inventory */}
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="space-y-1">
                <CardTitle className="text-lg">Current Shop Inventory ({activeShopInventory?.length || 0})</CardTitle>
                <p className="text-sm text-muted-foreground">
                  {!isEditMode 
                    ? 'Drag rows to reorder or use the ↑ button to move items to top' 
                    : 'Products currently in your inventory'}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                {!isEditMode ? (
                  <>
                    <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 border-blue-200 dark:border-blue-800">
                      <CardContent className="p-4">
                        <div className="flex flex-col items-center">
                          <span className="text-xs font-semibold text-blue-600 dark:text-blue-300 uppercase tracking-wider">Total Quantity</span>
                          <span className="text-2xl font-bold text-blue-900 dark:text-blue-100 mt-1">
                            {formatQuantity(activeShopInventory?.reduce((sum, item) => sum + (item.closingStock ?? item.totalStock ?? 0), 0) || 0)}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950 dark:to-green-900 border-green-200 dark:border-green-800">
                      <CardContent className="p-4">
                        <div className="flex flex-col items-center">
                          <span className="text-xs font-semibold text-green-600 dark:text-green-300 uppercase tracking-wider">Total Value (MRP)</span>
                          <span className="text-2xl font-bold text-green-900 dark:text-green-100 mt-1">
                            ₹{formatNumber(activeShopInventory?.reduce((sum, item) => sum + parseFloat(item.mrp) * (item.closingStock ?? item.totalStock ?? 0), 0) || 0)}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                    <Button 
                      size="sm"
                      variant="outline"
                      onClick={toggleEditMode}
                      disabled={!activeShopInventory || activeShopInventory.length === 0}
                      className="gap-2"
                    >
                      <Edit className="h-4 w-4" />
                      Edit Prices
                    </Button>
                  </>
                ) : (
                  <>
                    <Badge variant="default" className="px-3 py-2">
                      <span className="text-xs font-medium">Price Edit Mode</span>
                    </Badge>
                    <Button 
                      size="sm"
                      variant="default"
                      onClick={saveEditedItems}
                      disabled={saving || Object.keys(editedItems).length === 0}
                      className="gap-2"
                    >
                      <Save className="h-4 w-4" />
                      {saving ? 'Saving...' : 'Save Prices'}
                    </Button>
                    <Button 
                      size="sm"
                      variant="outline"
                      onClick={cancelEdit}
                      disabled={saving}
                      className="gap-2"
                    >
                      <X className="h-4 w-4" />
                      Cancel
                    </Button>
                  </>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {!activeShopInventory || activeShopInventory.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <PackageSearch className="h-16 w-16 text-muted-foreground mb-4" />
                <h4 className="text-lg font-semibold mb-2">No products in inventory</h4>
                <p className="text-sm text-muted-foreground">Use the search above to find and add products to your inventory</p>
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[80px] text-center">S.No</TableHead>
                      <TableHead className="text-center">Brand Details</TableHead>
                      <TableHead className="text-center">Pack Info</TableHead>
                      <TableHead className="text-center">MRP</TableHead>
                      <TableHead className="text-center">Markup</TableHead>
                      <TableHead className="text-center">
                        <div>
                          <div className="font-semibold">FINAL PRICE</div>
                          <div className="text-xs text-muted-foreground font-normal">(MRP + MARKUP)</div>
                        </div>
                      </TableHead>
                      <TableHead className="text-center">Current Stock</TableHead>
                      <TableHead className="text-center">Last Updated</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody ref={inventoryTableRef}>
                    {groupInventoryByBrand(activeShopInventory || []).map((brandGroup) => 
                      brandGroup.items.map((item, itemIndex) => {
                        const isDragging = draggedItem?.id === item.id;
                        const isDraggedOver = dragOverItem?.id === item.id;
                        return (
                          <TableRow 
                            key={item.id}
                            draggable={!isEditMode}
                            onDragStart={(e) => !isEditMode && handleDragStart(e, item)}
                            onDragOver={(e) => !isEditMode && handleDragOver(e, item)}
                            onDragEnd={handleDragEnd}
                            onDrop={(e) => !isEditMode && handleDrop(e, item)}
                            className={`
                              ${itemIndex === brandGroup.items.length - 1 ? 'border-b-2' : ''}
                              ${isDragging ? 'opacity-50' : ''}
                              ${isDraggedOver ? 'bg-accent' : ''}
                              ${!isEditMode ? 'cursor-move' : ''}
                            `}
                          >
                            {itemIndex === 0 && (
                              <>
                                <TableCell className="font-medium text-center" rowSpan={brandGroup.items.length}>
                                  <div className="flex items-center justify-center gap-1">
                                    {!isEditMode && (
                                      <GripVertical className="h-4 w-4 text-muted-foreground" title="Drag to reorder" />
                                    )}
                                    <span>{brandGroup.serialNumber}</span>
                                    <Button 
                                      size="icon"
                                      variant="ghost"
                                      className="h-6 w-6"
                                      title="Move to top"
                                      onClick={() => moveToTop(brandGroup.items[0].id)}
                                    >
                                      <ArrowUp className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </TableCell>
                                <TableCell className="font-medium text-center" rowSpan={brandGroup.items.length}>
                                  <div>
                                    <div className="font-semibold">{brandGroup.brandName}</div>
                                    <div className="text-xs text-muted-foreground">#{brandGroup.brandNumber}</div>
                                  </div>
                                </TableCell>
                              </>
                            )}
                            <TableCell className="text-center">
                              <div>
                                <div className="font-medium">{item.pack_quantity} × {item.size}ml</div>
                                <div className="text-xs text-muted-foreground">Type: {item.packType}</div>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">₹{formatNumber(parseFloat(item.mrp))}</TableCell>
                            <TableCell className="text-center">
                              {isEditMode ? (
                                <Input
                                  type="number"
                                  value={editedItems[item.id]?.markup !== undefined ? editedItems[item.id].markup : (parseFloat(item.markup_price) > 0 ? item.markup_price : '')}
                                  onChange={(e) => handleEditMarkupChange(item.id, e.target.value)}
                                  onFocus={(e) => {
                                    if (e.target.value === '') {
                                      e.target.placeholder = '';
                                    }
                                  }}
                                  onBlur={(e) => {
                                    if (e.target.value === '') {
                                      e.target.placeholder = '0';
                                    }
                                  }}
                                  onWheel={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    e.target.blur();
                                    setTimeout(() => e.target.focus(), 0);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }
                                  }}
                                  min="0"
                                  step="0.01"
                                  className="w-24 text-center"
                                  placeholder="0"
                                  inputMode="decimal"
                                />
                              ) : (
                                <span>₹{formatNumber(parseFloat(item.markup_price || 0))}</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">₹{formatNumber(parseFloat(item.mrp) + getMarkupValue(item.id, item.markup_price))}</TableCell>
                            <TableCell className="text-center">
                              <Badge variant="secondary">{item.closingStock ?? item.totalStock ?? 0}</Badge>
                            </TableCell>
                            <TableCell className="text-center text-sm text-muted-foreground">
                              {item.last_updated ? new Date(item.last_updated).toLocaleDateString() : 'N/A'}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

          {/* Selected products for onboarding */}
          {selectedProducts.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Selected Products ({selectedProducts.length})</CardTitle>
                  <Badge variant="default" className="px-3 py-2">
                    <span className="text-xs font-medium">Total Value: ₹{formatNumber(selectedProducts.reduce((sum, product) => sum + (product.quantity * (parseFloat(product.mrp) + parseFloat(product.markup))), 0))}</span>
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[60px]">S.No</TableHead>
                        <TableHead>Brand Details</TableHead>
                        <TableHead>Pack Info</TableHead>
                        <TableHead className="text-right">MRP</TableHead>
                        <TableHead className="text-right">Quantity</TableHead>
                        <TableHead className="text-right">Markup</TableHead>
                        <TableHead className="text-right">Final Price</TableHead>
                        <TableHead className="text-right">Total Value</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                    {selectedProducts.map((product, index) => {
                      const finalPrice = parseFloat(product.mrp) + parseFloat(product.markup);
                      const totalValue = product.quantity * finalPrice;
                      return (
                        <TableRow key={product.id}>
                          <TableCell className="font-medium">{index + 1}</TableCell>
                          <TableCell>
                            <div>
                              <div className="font-semibold">{product.name}</div>
                              <div className="text-xs text-muted-foreground">#{product.brandNumber}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <div className="font-medium">{product.packQuantity} × {product.size}ml</div>
                              <div className="text-xs text-muted-foreground">Type: {product.packType}</div>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">₹{product.mrp}</TableCell>
                          <TableCell className="text-right">
                            <Input
                              type="number"
                              value={product.quantity === 0 ? '' : product.quantity}
                              onChange={(e) => handleQuantityChange(product.id, e.target.value)}
                              onFocus={(e) => {
                                if (e.target.value === '') {
                                  e.target.placeholder = '';
                                }
                              }}
                              onBlur={(e) => {
                                if (e.target.value === '') {
                                  e.target.placeholder = '0';
                                }
                              }}
                              onWheel={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }
                              }}
                              min="0"
                              className="w-24 text-right"
                              placeholder="0"
                              inputMode="numeric"
                            />
                          </TableCell>
                          <TableCell className="text-right">
                            <Input
                              type="number"
                              value={product.markup === 0 ? '' : product.markup}
                              onChange={(e) => handleMarkupChange(product.id, e.target.value)}
                              onFocus={(e) => {
                                e.target.placeholder = '';
                              }}
                              onBlur={(e) => {
                                if (e.target.value === '') {
                                  e.target.placeholder = '0';
                                }
                              }}
                              onWheel={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }
                              }}
                              min="0"
                              step="0.01"
                              className="w-24 text-right"
                              placeholder="0"
                              inputMode="decimal"
                            />
                          </TableCell>
                          <TableCell className="text-right">₹{finalPrice.toFixed(2)}</TableCell>
                          <TableCell className="text-right">₹{formatNumber(totalValue)}</TableCell>
                          <TableCell className="text-right">
                            <Button 
                              size="sm"
                              variant="destructive"
                              onClick={() => removeProduct(product.id)}
                              className="gap-2"
                            >
                              <Trash2 className="h-4 w-4" />
                              Remove
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

        {selectedProducts.length > 0 && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                <div className="flex flex-wrap gap-4">
                  <div className="flex flex-col items-center px-4 py-2 rounded-lg bg-muted">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Total Products</span>
                    <span className="text-2xl font-bold mt-1">{selectedProducts.length}</span>
                  </div>
                  <div className="flex flex-col items-center px-4 py-2 rounded-lg bg-muted">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Total Quantity</span>
                    <span className="text-2xl font-bold mt-1">{formatQuantity(selectedProducts.reduce((sum, product) => sum + product.quantity, 0))}</span>
                  </div>
                  <div className="flex flex-col items-center px-4 py-2 rounded-lg bg-muted">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Total Value</span>
                    <span className="text-2xl font-bold mt-1">₹{formatNumber(selectedProducts.reduce((sum, product) => sum + (product.quantity * (parseFloat(product.mrp) + parseFloat(product.markup))), 0))}</span>
                  </div>
                </div>
                <Button 
                  size="lg"
                  onClick={handleSaveStockOnboarding}
                  disabled={saving}
                  className="gap-2 w-full sm:w-auto"
                >
                  <Save className="h-5 w-5" />
                  {saving ? 'Saving...' : 'Save Stock Onboarding'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
  );
}

export default StockOnboarding;